import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "~/app/store/baseApi";
import { type WishlistDTO } from "~/features/wishlist/types/dto";
import { type WishlistsOnlyResponse, transformResponse } from "~/features/wishlist/api/fetchWishlists";
import { type WishlistEntryModel } from "~/features/wishlist/types/model";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";

interface WishlistEntryLookupParams {
  productCode: string;
  wishlistEntryNumber: number;
}

interface UpdateWishlistEntryParams extends WishlistEntryLookupParams {
  id: string;
  quantity?: number;
  comment?: string;
  priceReference?: string;
  optionalParts?: { productCode: string; quantity: number }[];
  newProductCode?: string;
}

const normalizeWishlistEntryNumber = (wishlistEntryNumber?: number) => wishlistEntryNumber ?? 0;

const matchesWishlistEntry = (entry: WishlistEntryModel, lookup: WishlistEntryLookupParams): boolean => {
  const lookupWishlistEntryNumber = normalizeWishlistEntryNumber(lookup.wishlistEntryNumber);
  const entryWishlistEntryNumber = normalizeWishlistEntryNumber(entry.wishlistEntryNumber);

  if (lookupWishlistEntryNumber > 0) {
    return entryWishlistEntryNumber === lookupWishlistEntryNumber && entry.productCode === lookup.productCode;
  }

  return entryWishlistEntryNumber <= 0 && entry.productCode === lookup.productCode;
};

export const wishlistApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWishlists: builder.query<WishlistsOnlyResponse, void>({
      /**
       * Uses queryFn to manually control caching:
       * - Products are dispatched to productsReducer via addBaseProductDataAction
       * - Only wishlists + wishlistEntries are cached by RTK Query
       * This prevents duplicate storage of product data.
       *
       * NOTE: We use baseQuery (4th arg) instead of raw fetch to inherit
       * baseUrl (with language code) and headers from baseApi.
       */
      queryFn: async (_, { dispatch }, _extraOptions, baseQuery) => {
        const result = await baseQuery({ url: "/wishlists" });

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const data = result.data as WishlistDTO[];
        const transformed = transformResponse(data);

        // Dispatch products to products reducer (not cached by RTK Query)
        dispatch(addBaseProductDataAction(transformed.products));

        // Return only wishlists + entries for RTK Query cache
        return {
          data: {
            wishlists: transformed.wishlists,
            wishlistEntries: transformed.wishlistEntries,
          },
        };
      },
      providesTags: ["Wishlist"],
    }),

    // ========================================================================
    // OPTIMISTIC MUTATIONS (no refetch - patch cache directly)
    // ========================================================================

    deleteWishlist: builder.mutation<void, string>({
      query: (wishlistId) => ({
        url: `/wishlist/delete?id=${wishlistId}`,
        method: "DELETE",
      }),
      async onQueryStarted(wishlistId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            const wishlist = draft.wishlists.find((wl) => wl.id === wishlistId);
            if (wishlist) {
              // Remove all entries belonging to this wishlist
              draft.wishlistEntries = draft.wishlistEntries.filter((entry) => !wishlist.entryIds.includes(entry.id));
              // Remove the wishlist
              draft.wishlists = draft.wishlists.filter((wl) => wl.id !== wishlistId);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    removeEntry: builder.mutation<void, { id: string } & WishlistEntryLookupParams>({
      query: (data) => ({
        url: "/wishlist/removeEntry",
        method: "POST",
        body: {
          ...data,
          wishlistEntryNumber: normalizeWishlistEntryNumber(data.wishlistEntryNumber),
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            // Find and remove the entry
            const entryIndex = draft.wishlistEntries.findIndex((entry) => matchesWishlistEntry(entry, data));
            if (entryIndex !== -1) {
              const entryId = draft.wishlistEntries[entryIndex].id;
              draft.wishlistEntries.splice(entryIndex, 1);

              // Remove entry ID from wishlist's entryIds
              const wishlist = draft.wishlists.find((wl) => wl.id === data.id);
              if (wishlist) {
                wishlist.entryIds = wishlist.entryIds.filter((id) => id !== entryId);
              }
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateWishlistEntry: builder.mutation<void, UpdateWishlistEntryParams>({
      query: (data) => ({
        url: "/wishlist/updateEntry",
        method: "POST",
        body: {
          id: data.id,
          productCode: data.productCode,
          quantity: data.quantity,
          wishlistEntryNumber: normalizeWishlistEntryNumber(data.wishlistEntryNumber),
          comment: data.comment,
          priceReference: data.priceReference,
          optionalParts: data.optionalParts,
          newProductCode: data.newProductCode,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            const entry = draft.wishlistEntries.find((wishlistEntry) => matchesWishlistEntry(wishlistEntry, data));
            if (entry) {
              if (data.quantity !== undefined) entry.quantity = data.quantity;
              if (data.comment !== undefined) entry.comment = data.comment;
              if (data.priceReference !== undefined) entry.priceReference = data.priceReference;
              if (data.optionalParts !== undefined) entry.optionalParts = data.optionalParts;
              if (data.newProductCode !== undefined) entry.productCode = data.newProductCode;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    createOrUpdateWishlist: builder.mutation<void, { id?: string; wishlistName: string; description?: string }>({
      query: (data) => ({
        url: "/wishlist/createOrUpdate",
        method: "POST",
        body: data,
      }),
      // Conditionally invalidate or patch based on whether this is create or update
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        if (data.id) {
          // UPDATE mode (rename/update comment) - optimistic patch
          const patchResult = dispatch(
            wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
              const wishlist = draft.wishlists.find((wl) => wl.id === data.id);
              if (wishlist) {
                wishlist.name = data.wishlistName;
                if (data.description !== undefined) {
                  wishlist.comment = data.description;
                }
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        } else {
          // CREATE mode - must refetch to get server-generated ID
          try {
            await queryFulfilled;
            dispatch(wishlistApi.util.invalidateTags(["Wishlist"]));
          } catch {
            // Error handled by RTK Query
          }
        }
      },
    }),

    // ========================================================================
    // MUTATIONS THAT REQUIRE REFETCH (server generates IDs)
    // ========================================================================

    addEntry: builder.mutation<
      void,
      {
        id?: string;
        wishlistName?: string;
        productCode: string;
        quantity: number;
        priceReference?: string;
        optionalParts?: { productCode: string; quantity: number }[];
      }
    >({
      query: (data) => ({
        url: "/wishlist/addEntry",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Wishlist"],
    }),

    addCart: builder.mutation<void, { id: string } | { wishlistName: string }>({
      query: (data) => ({
        url: "/wishlist/addCart",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Wishlist"],
    }),

    bulkAddEntry: builder.mutation<
      void,
      { wishlistId?: string; wishlistName?: string; products: { code: string; quantity: number }[] }
    >({
      query: (data) => ({
        url: "/wishlist/addEntry/bulk",
        method: "POST",
        body: {
          id: data.wishlistId,
          wishlistName: !data.wishlistId ? data.wishlistName : undefined,
          products: data.products,
        },
      }),
      invalidatesTags: ["Wishlist"],
    }),
  }),
});

export const {
  useGetWishlistsQuery,
  useDeleteWishlistMutation,
  useRemoveEntryMutation,
  useCreateOrUpdateWishlistMutation,
  useUpdateWishlistEntryMutation,
  useAddEntryMutation,
  useAddCartMutation,
  useBulkAddEntryMutation,
} = wishlistApi;
